import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import axios from "axios";
import Layout from "../components/layout/ChartsLayout.jsx";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import config from "../config.js";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);


const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const Sales = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/convertBalanceSheetToJson`);
        
        if (response.data && response.data.data && response.data.data.Sheet1) {
          setData(response.data.data.Sheet1);
        } else if (response.data && response.data.Sheet1) {
          setData(response.data.Sheet1);
        } else {
         
          throw new Error("Unexpected data structure");
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to fetch data");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom align="center">
            Financial Dashboard
          </Typography>
          <Alert severity="error" sx={{ mt: 4 }}>
            No data found. Please check your connection and try again.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Error details: {error}
          </Typography>
        </Box>
      </Layout>
    );
  }
  
  const groupedData = data
    ? data.reduce((acc, item) => {
        if (item.Category) {
          if (!acc[item.Category]) {
            acc[item.Category] = [];
          }
          if (item.Item) {
            acc[item.Category].push(item);
          } else {
            acc[item.Category].total = item["Amount (INR)"];
          }
        }
        return acc;
      }, {})
    : {};


  const assetData = {
    labels: data
      ? data
          .filter(
            (item) =>
              (item.Category === "Current Assets" ||
                item.Category === "Non-Current Assets") &&
              item.Item
          )
          .map((item) => item.Item)
      : [],
    datasets: [
      {
        label: "Assets",
        data: data
          ? data
              .filter(
                (item) =>
                  (item.Category === "Current Assets" ||
                    item.Category === "Non-Current Assets") &&
                  item.Item
              )
              .map((item) => item["Amount (INR)"])
          : [],
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
          "rgba(255, 206, 86, 0.6)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const liabilityData = {
    labels: data
      ? data
          .filter(
            (item) =>
              (item.Category === "Current Liabilities" ||
                item.Category === "Non-Current Liabilities") &&
              item.Item
          )
          .map((item) => item.Item)
      : [],
    datasets: [
      {
        label: "Liabilities",
        data: data
          ? data
              .filter(
                (item) =>
                  (item.Category === "Current Liabilities" ||
                    item.Category === "Non-Current Liabilities") &&
                  item.Item
              )
              .map((item) => item["Amount (INR)"])
          : [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const balanceSheetSummary = {
    labels: ["Total Assets", "Total Liabilities", "Total Equity"],
    datasets: [
      {
        label: "Balance Sheet",
        data: [
          groupedData["Total Assets"]?.total || 0,
          groupedData["Total Liabilities"]?.total || 0,
          groupedData["Total Equity"]?.total || 0,
        ],
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 99, 132, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom align="center">
            Financial Dashboard
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            align="center"
            gutterBottom
          >
            Balance Sheet Analysis - March 2025
          </Typography>

          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  sx={{ bgcolor: "#1976d2", color: "white", height: "100%" }}
                >
                  <CardContent>
                    <Typography variant="h6">Total Assets</Typography>
                    <Typography variant="h4" sx={{ mt: 2 }}>
                      {formatCurrency(groupedData["Total Assets"]?.total || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Current:{" "}
                      {formatCurrency(
                        groupedData["Total Current Assets"]?.total || 0
                      )}
                      <br />
                      Non-Current:{" "}
                      {formatCurrency(
                        groupedData["Total Non-Current Assets"]?.total || 0
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card
                  sx={{ bgcolor: "#dc004e", color: "white", height: "100%" }}
                >
                  <CardContent>
                    <Typography variant="h6">Total Liabilities</Typography>
                    <Typography variant="h4" sx={{ mt: 2 }}>
                      {formatCurrency(
                        groupedData["Total Liabilities"]?.total || 0
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Current:{" "}
                      {formatCurrency(
                        groupedData["Total Current Liabilities"]?.total || 0
                      )}
                      <br />
                      Non-Current:{" "}
                      {formatCurrency(
                        groupedData["Total Non-Current Liabilities"]?.total || 0
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card
                  sx={{ bgcolor: "#4caf50", color: "white", height: "100%" }}
                >
                  <CardContent>
                    <Typography variant="h6">Total Equity</Typography>
                    <Typography variant="h4" sx={{ mt: 2 }}>
                      {formatCurrency(groupedData["Total Equity"]?.total || 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Share Capital:{" "}
                      {formatCurrency(
                        data?.find((item) => item.Item === "Share Capital")?.[
                          "Amount (INR)"
                        ] || 0
                      )}
                      <br />
                      Retained Earnings:{" "}
                      {formatCurrency(
                        data?.find(
                          (item) => item.Item === "Retained Earnings"
                        )?.["Amount (INR)"] || 0
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Paper sx={{ p: 3, height: 360 }}>
                  <Typography variant="h6" gutterBottom>
                    Assets Breakdown
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300, position: "relative" }}>
                    <Doughnut
                      data={assetData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: "right",
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Paper sx={{ p: 3, height: 360 }}>
                  <Typography variant="h6" gutterBottom>
                    Liabilities Breakdown
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300, position: "relative" }}>
                    <Pie
                      data={liabilityData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: "right",
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12}>
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Paper sx={{ p: 3, height: 360 }}>
                  <Typography variant="h6" gutterBottom>
                    Balance Sheet Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ height: 300, position: "relative" }}>
                    <Bar
                      data={balanceSheetSummary}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </motion.div>
    </Layout>
  );
};

export default Sales;
